import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { responseHandler } from "@msuite/ui/utils";
import { type ResourceLinkFormSchema, resourceLinkSchema } from "@msuite/utils/schemas";
import { FormProvider, useForm } from "react-hook-form";
import { ResourceLinkForm } from "@/forms/resource-link-form";
import { client } from "@/hono";

/** Props Interface */
interface AddResourceLinkModalProps extends ModalProps {
	projectId?: string;
	ticketId?: string | null;
	hideTicketPicker?: boolean;
	withoutPersistence?: boolean;
	onComplete: (json: ResourceLinkFormSchema) => Promise<void> | void;
}

export function AddResourceLinkModal({
	isOpen,
	onClose,
	projectId,
	ticketId,
	hideTicketPicker = false,
	withoutPersistence = false,
	onComplete,
}: AddResourceLinkModalProps) {
	/** Hooks */
	const { t } = useTranslation();
	const methods = useForm<ResourceLinkFormSchema>({
		resolver: resolver(resourceLinkSchema),
		defaultValues: {
			link: "",
			name: null,
			ticketId: ticketId ?? null,
		},
	});

	/** Functions */
	async function handleOnSubmit(json: ResourceLinkFormSchema) {
		try {
			if (!withoutPersistence) {
				if (!projectId) throw new Error("Project id is required to persist a resource link");
				const response = await client.projects[":projectId"]["resource-links"].$post({
					param: { projectId },
					json,
				});
				await responseHandler(response);
			}
			await onComplete(json);
			onClose();
			methods.reset();
		} catch (error) {
			console.error(error);
		}
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				isOpen={isOpen}
				onClose={onClose}
				title={t("resource_links.add_resource_link")}
				width="500px"
				onSubmit={methods.handleSubmit(handleOnSubmit)}
				isLoading={methods.formState.isLoading}
				data-cy="add-resource-link-modal"
			>
				<Modal.Body>
					<ResourceLinkForm
						projectId={projectId}
						hideTicketPicker={hideTicketPicker}
					/>
				</Modal.Body>
				<Modal.FooterForm />
			</Modal>
		</FormProvider>
	);
}
